import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Eyebrow } from './Eyebrow';
import type { PillarPageContent } from '../types/pillarPage';
type HeroProps = {
  content: PillarPageContent['hero'];
};
export function Hero({
  content
}: HeroProps) {
  const reduced = useReducedMotion();
  return <header className="relative isolate flex min-h-[100svh] w-full flex-col justify-end overflow-hidden bg-ink" data-id="element-118" data-mp="4jm6npu5l5rm">
      <img src={content.image} alt={content.imageAlt} className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-70" data-id="element-119" data-mp="xae9h65q4snx" />
      <div className="absolute inset-0 bg-ink/55" aria-hidden="true" data-id="element-120" data-mp="s9ghy2ytqm9r" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#080909_0%,rgba(8,9,9,0.92)_38%,rgba(8,9,9,0.35)_70%,rgba(8,9,9,0.75)_100%)]" aria-hidden="true" data-id="element-121" data-mp="nb6mrsd3nlo5" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-[linear-gradient(to_top,#080909,transparent)]" aria-hidden="true" data-id="element-122" data-mp="3825wymt9u1js" />

      <div className="relative mx-auto w-full max-w-[1240px] px-6 pb-28 pt-40 sm:px-10 lg:pb-36" data-id="element-123" data-mp="3j6fw9kdl53b7">
        <motion.div initial={reduced ? undefined : {
        opacity: 0,
        y: 22
      }} animate={reduced ? undefined : {
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="max-w-3xl" data-id="element-124" data-mp="3o5j2371e3dk8">
          <Eyebrow data-id="element-125" data-mp="30gzvcvxzn8u9">{content.eyebrow}</Eyebrow>

          <h1 className="mt-7 text-[2.6rem] font-extrabold leading-[1.03] tracking-[-0.03em] text-bone sm:text-6xl lg:text-[4.7rem]" data-id="element-126" data-mp="30g6m9obk478y">
            {content.headlineTop}
            <span className="block text-accent" data-id="element-127" data-mp="2qleoaje2bidn">{content.headlineAccent}</span>
          </h1>

          <p className="mt-8 max-w-xl text-base font-normal leading-relaxed text-ash sm:text-lg" data-id="element-128" data-mp="2vjpvemcz3es6">
            {content.quote}
          </p>

          <div className="mt-11 flex flex-col items-stretch gap-5 sm:flex-row sm:items-center" data-id="element-129" data-mp="2lkqzm4db3dml">
            <a href="#perspectiva" className="group inline-flex items-center justify-center gap-3 rounded-2xl bg-accent px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-accent-ink shadow-[0_18px_44px_-24px_rgb(var(--accent)/0.85)] transition-[transform,filter] duration-200 ease-elite hover:-translate-y-0.5 hover:brightness-110" data-id="element-130" data-mp="2lmlcqwic61g4">
              {content.ctaLabel}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-elite group-hover:translate-x-1" aria-hidden="true" data-id="element-131" data-mp="1b1ffr3qos6v8" />
            </a>
          </div>

          <ul className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-medium uppercase tracking-label text-ash/80" data-id="element-132" data-mp="111ok2yqe3ltk">
            {content.tags.map((tag, i) => <li key={tag} className="flex items-center gap-3" data-id="element-133" data-mp="3ltgo5tkf8wpx" data-mpi={i}>
                {i > 0 && <span className="h-1 w-1 rounded-full bg-accent/60" aria-hidden="true" data-id="element-134" data-mp="3hrd80rmt53yb" />}
                <span data-id="element-135" data-mp="3htpyjt6msmmz">{tag}</span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      <div className="relative mx-auto w-full max-w-[1240px] px-6 pb-10 sm:px-10" data-id="element-136" data-mp="21awdua25atgy">
        <div className="flex items-center gap-4" data-id="element-137" data-mp="218jvvwwe0j3w">
          <span className="relative block h-12 w-px overflow-hidden bg-accent/20" aria-hidden="true" data-id="element-138" data-mp="268y53i9q5z3i">
            <motion.span className="absolute inset-x-0 top-0 block h-5 bg-accent" animate={reduced ? undefined : {
            y: ['-100%', '240%']
          }} transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-139" data-mp="2b1r8a1pjozdh" />
          </span>
          <span className="text-[10px] font-medium uppercase tracking-label text-ash/60" data-id="element-140" data-mp="2ozz589ml00on">
            Desliza
          </span>
        </div>
      </div>
    </header>;
}